import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { getDataAPI } from '../utils/fetchData';
import { getActiveRemindersCount } from '../utils/remindersData';
import { activityFeedApi } from '../utils/activityFeedApi';
import ActivityFeed from './ActivityFeed';

// Subtle modern animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const gentlePulse = keyframes`
  0%, 100% { 
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.1);
  }
  50% { 
    box-shadow: 0 0 0 8px rgba(255, 255, 255, 0.05);
  }
`;

// Modern dashboard container
const ModernDashboard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  transition: all 0.3s ease;
  margin-top: 40px;
  
  @media (max-width: 480px) {
    gap: 20px;
    margin-top: 40px;
  }
`;

// Header section with period selector
const DashboardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  
  @media (max-width: 480px) {
    margin-bottom: 20px;
  }
`;

const TimeFilterDropdown = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const DropdownButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.7;
  }
`;

const DropdownArrow = styled.svg`
  width: 16px;
  height: 16px;
  color: rgba(255, 255, 255, 0.6);
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: all 0.2s ease;
  margin-left: 4px;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 8px;
  background: rgba(0, 0, 0, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  overflow: hidden;
  z-index: 1000;
  backdrop-filter: blur(10px);
  min-width: 120px;
`;

const DropdownItem = styled.div`
  padding: 12px 16px;
  color: white;
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: opacity 0.2s ease;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    opacity: 0.7;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
`;

const QuickAction = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;
  padding: 0;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.7;
  }
`;

// Unified metrics grid
const MetricsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

// Unified card style for all metrics
const UnifiedCard = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.2s ease;
  animation: ${fadeInUp} 0.5s ease-out;
  animation-delay: ${props => props.index * 0.05}s;
  animation-fill-mode: both;
  width: 100%;
  box-sizing: border-box;
  
  &:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.15);
    transform: translateX(4px);
  }
`;

const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CardTitle = styled.div`
  color: white;
  font-size: 13px;
  font-weight: 600;
`;

const CardSubtitle = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CardValue = styled.div`
  color: ${props => props.color};
  font-size: 18px;
  font-weight: 700;
  text-shadow: 0 0 10px ${props => props.color}40;
`;

const CollapsibleContent = styled.div`
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: ${props => props.isCollapsed ? '0' : '800px'};
  opacity: ${props => props.isCollapsed ? '0' : '1'};
`;

const DashboardStats = ({ isCollapsed = false }) => {
  const [timePeriod, setTimePeriod] = useState('28D');
  const [totalCounts, setTotalCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const [isLoadingData, setIsLoadingData] = useState(false); // Add loading guard

  const timePeriodOptions = [
    { key: '7D', label: '7 Days', display: '7 Days' },
    { key: '28D', label: '28 Days', display: '28 Days' },
    { key: '3M', label: '3 Months', display: '3 Months' },
    { key: 'ALL', label: 'All Time', display: 'All Time' }
  ];

  // Save selected period to localStorage when component mounts
  useEffect(() => {
    const savedPeriod = localStorage.getItem('dashboardTimePeriod');
    if (savedPeriod) {
      setTimePeriod(savedPeriod);
    }
  }, []);

  // Load data when timePeriod changes, but only once on mount and when period actually changes
  useEffect(() => {
    if (!isLoadingData) {
      loadActivityData();
    }
  }, [timePeriod]);

  // Consolidated useEffect for periodic updates and visibility changes
  useEffect(() => {
    // Initial load
    loadActivityData();
    
    // Update every 2 minutes (reduced frequency to prevent loops)
    const interval = setInterval(() => {
      if (!isLoadingData && !document.hidden) {
        loadActivityData();
      }
    }, 120000); // 2 minutes instead of 30 seconds
    
    // Handle visibility change
    const handleVisibilityChange = () => {
      if (!document.hidden && !isLoadingData) {
        loadActivityData();
      }
    };
    
    // Listen for reminder updates to refresh dashboard
    const handleStorageChange = (e) => {
      if (e.key === 'dizee_reminders' && !isLoadingData) {
        console.log('📡 Dashboard detected reminder changes, refreshing...');
        loadActivityData();
      }
    };

    const handleCustomStorageEvent = (e) => {
      if (e.detail?.key === 'dizee_reminders' && !isLoadingData) {
        console.log('📡 Dashboard detected custom reminder changes, refreshing...');
        loadActivityData();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('localStorageUpdate', handleCustomStorageEvent);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageUpdate', handleCustomStorageEvent);
    };
  }, []); // Only run once on mount

  const getCurrentPeriodDisplay = () => {
    const current = timePeriodOptions.find(option => option.key === timePeriod);
    return current ? current.display : '28 Days';
  };

  const handlePeriodSelect = (periodKey) => {
    setTimePeriod(periodKey);
    setDropdownOpen(false);
    // Save selected period to localStorage
    localStorage.setItem('dashboardTimePeriod', periodKey);
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const loadActivityData = async () => {
    // Prevent multiple simultaneous calls
    if (isLoadingData) {
      console.log('⏸️ Dashboard data already loading, skipping...');
      return;
    }
    
    setIsLoadingData(true);
    setLoading(true); // Keep original loading state for UI
    
    try {
      // Get real data from activity feed API and reminders
      const [activityData] = await Promise.all([
        activityFeedApi.getActivityFeed()
      ]);
      
      // Get actual reminders count
      const actualActiveReminders = getActiveRemindersCount();
      
      // Use catalog data from activityFeedApi instead of making another API call
      let unreleasedSongsCount = 0;
      if (activityData.success && activityData.counts.catalog) {
        // For now, use the catalog count as unreleased songs count
        // TODO: Enhance activityFeedApi to differentiate released vs unreleased
        unreleasedSongsCount = activityData.counts.catalog;
      }
      
      // Use real data from API, fallback to 0 if not available
      const counts = {
        activeReminders: actualActiveReminders,
        pendingBookings: activityData.success ? (activityData.counts.bookings || 0) : 0,
        catalogItems: unreleasedSongsCount,
        merchItems: activityData.success ? (activityData.counts.merch || 0) : 0,
        playlistAdds: activityData.success ? (activityData.counts.playlist || 0) : 0,
        chartAdds: activityData.success ? (activityData.counts.charting || 0) : 0
      };
      setTotalCounts(counts);
    } catch (error) {
      console.error('Failed to load data:', error);
      // Fallback to at least getting the correct reminders count
      const actualActiveReminders = getActiveRemindersCount();
      setTotalCounts({
        activeReminders: actualActiveReminders,
        pendingBookings: 0,
        catalogItems: 0,
        merchItems: 0,
        playlistAdds: 0,
        chartAdds: 0
      });
    } finally {
      setIsLoadingData(false); // Reset loading guard
      setLoading(false); // Reset UI loading state
    }
  };

  const getAllMetrics = () => {
    if (!totalCounts) return [];
    
    const allMetrics = [
      // Priority items first
      ...(totalCounts.activeReminders > 5 ? [{
        title: 'Active Workflows',
        subtitle: 'Require attention',
        value: totalCounts.activeReminders,
        color: '#FF3B30',
        urgent: true,
        onClick: () => navigate('/flows')
      }] : []),
      // Always show pending bookings (even if 0)
      {
        title: 'Pending Bookings',
        subtitle: 'Awaiting confirmation',
        value: totalCounts.pendingBookings,
        color: '#FFFF00',
        onClick: () => navigate('/bookings')
      },
      // Other metrics (excluding duplicates)
      {
        title: 'Unreleased Songs',
        subtitle: 'Coming soon',
        value: totalCounts.catalogItems,
        color: '#17A2B8',
        onClick: () => navigate('/catalog')
      },
      {
        title: 'Your biggest playlists have',
        subtitle: 'Tracked playlists',
        value: totalCounts.playlistAdds,
        color: '#5856D6',
        onClick: () => navigate('/playlisting')
      },
      {
        title: 'Charts',
        subtitle: 'Chart entries',
        value: totalCounts.chartAdds,
        color: '#FF9500',
        onClick: () => navigate('/charting')
      }
    ];

    return allMetrics;
  };

  const metrics = getAllMetrics();

  return (
    <ModernDashboard>
      <DashboardHeader>
        <TimeFilterDropdown>
          <DropdownButton onClick={toggleDropdown}>
            {getCurrentPeriodDisplay()}
            <DropdownArrow isOpen={dropdownOpen} fill="none" viewBox="0 0 24 24">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="m7 10 5 5 5-5"
              />
            </DropdownArrow>
          </DropdownButton>
          
          {dropdownOpen && (
            <>
              <Overlay onClick={() => setDropdownOpen(false)} />
              <DropdownMenu>
                {timePeriodOptions.map((option) => (
                  <DropdownItem
                    key={option.key}
                    onClick={() => handlePeriodSelect(option.key)}
                  >
                    {option.label}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </>
          )}
        </TimeFilterDropdown>
        
        <QuickAction onClick={() => navigate('/activity-feed')}>
          View All
        </QuickAction>
      </DashboardHeader>

      <CollapsibleContent isCollapsed={isCollapsed}>
        {/* Activity Feed Component */}
        <ActivityFeed isCollapsed={isCollapsed} />
      </CollapsibleContent>
    </ModernDashboard>
  );
};

export default DashboardStats; 